import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import SEO from '../common/SEO';
import GoogleMaps from '../components/GoogleMaps';
import ServicesNavigationFooter from '../components/ServicesNavigationFooter';
import * as FiIcons from 'react-icons/fi';

const { FiPhone, FiMail, FiMapPin, FiClock, FiFileText, FiArrowRight, FiCopy, FiCheck } = FiIcons;

const CopyableTemplate = () => {
  const [copied, setCopied] = useState(false);
  const templateText = `Ort: Erkelenz
Gebäude: Einfamilienhaus, Altbau ca. 1970
Leistung: Trockenbauwand ca. 4,20 m × 2,60 m, einseitig beplankt
Besonderes: Schallschutz erwünscht
Zustand: Raum leer, Boden gefliest
Zeitraum: innerhalb der nächsten 6–8 Wochen
Fotos: siehe Anhang`;

  const handleCopy = () => {
    navigator.clipboard.writeText(templateText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-gray-900 rounded-xl p-6 shadow-xl relative overflow-hidden text-left mt-6 md:mt-0">
      <div className="absolute top-0 right-0 p-4">
        <button 
          onClick={handleCopy}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-bold text-sm transition-all ${copied ? 'bg-green-500 text-white' : 'bg-white/10 text-white hover:bg-white/20'}`}
        >
          <SafeIcon icon={copied ? FiCheck : FiCopy} />
          <span>{copied ? 'Kopiert!' : 'Vorlage kopieren'}</span>
        </button>
      </div>
      <h3 style={{ color: 'rgb(255, 255, 255)' }} className="text-white font-bold mb-4 flex items-center">
        <SafeIcon icon={FiFileText} className="mr-2 text-mk-teal-400"/>
        Muster-Anfrage
      </h3>
      <pre className="font-mono text-gray-300 whitespace-pre-wrap bg-black/30 p-4 rounded-lg text-sm border border-gray-700">
        {templateText}
      </pre>
      <p className="text-gray-500 text-xs mt-3 italic">
        👉 Klicken Sie auf "Kopieren" und fügen Sie den Text in das Nachrichtenfeld unten ein.
      </p>
    </div>
  );
};

function Contact() {
  const contactInfo = [
    {
      icon: FiPhone,
      title: 'Telefon',
      content: '+49 (0) 2452 8607038',
      description: 'Mo-Fr: 7:00 - 17:00 Uhr',
      link: 'tel:+4924528607038',
      clickable: true
    },
    {
      icon: FiMail,
      title: 'E-Mail',
      content: 'info@ausbau.nrw',
      description: 'Wir antworten innerhalb von 24h',
      link: 'mailto:info@ausbau.nrw',
      clickable: true
    },
    {
      icon: FiMapPin,
      title: 'Adresse',
      content: 'Am Diebsweg 17',
      description: '52525 Heinsberg',
      link: 'https://www.google.com/maps/dir//Am+Diebsweg+17,+52525+Heinsberg/@51.040924,6.0892095,12z/data=!4m8!4m7!1m0!1m5!1m1!1s0x47c0af825f62c6a1:0x3902b563a6cb20b0!2m2!1d6.1716098!2d51.040953?hl=de&authuser=0&entry=ttu&g_ep=EgoyMDI1MDgxOC4wIKXMDSoASAFQAw%3D%3D',
      clickable: true
    },
    {
      icon: FiClock,
      title: 'Öffnungszeiten',
      content: 'Mo-Fr: 7:00 - 17:00',
      description: 'Samstag nach Vereinbarung',
      clickable: false
    }
  ];

  return (
    <div className="py-20">
      <SEO 
        title="Kontakt | MK-AUSBAU Heinsberg" 
        description="Kontaktieren Sie uns für Ihr Bauvorhaben! Telefon, E-Mail & Kontaktformular. Kostenlose Beratung für Innenausbau & Sanierung in Heinsberg." 
        canonical="/kontakt"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Kontakt
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Haben Sie Fragen zu unseren Dienstleistungen oder möchten Sie ein unverbindliches Angebot erhalten? Nehmen Sie gerne Kontakt mit uns auf!
          </p>
        </motion.div>

        {/* Contact Information */}
        <div className="max-w-4xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-12"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Kontaktinformationen
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {contactInfo.map((info, index) => (
                <motion.div 
                  key={info.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                  className={`flex items-start space-x-4 p-6 bg-white rounded-xl shadow-lg ${info.clickable ? 'hover:shadow-xl transition-shadow' : ''}`}
                >
                  <div className="w-12 h-12 bg-mk-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={info.icon} className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {info.title}
                    </h3>
                    {info.clickable ? (
                      <a 
                        href={info.link} 
                        target={info.title === 'Adresse' ? '_blank' : undefined}
                        rel={info.title === 'Adresse' ? 'noopener noreferrer' : undefined}
                        className="text-gray-800 font-medium mb-1 hover:text-mk-teal-600 transition-colors block"
                      >
                        {info.content}
                      </a>
                    ) : (
                      <p className="text-gray-800 font-medium mb-1">
                        {info.content}
                      </p>
                    )}
                    <p className="text-gray-600 text-sm">
                      {info.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Hinweis auf Trockenbau-Guide & Copyable Template */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mb-12"
          >
            <div className="bg-gradient-to-r from-mk-teal-50 to-white border border-mk-teal-100 rounded-xl p-8 shadow-md">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-8 mb-8">
                <div className="flex-shrink-0 w-16 h-16 bg-mk-teal-100 text-mk-teal-600 rounded-full flex items-center justify-center shadow-sm mx-auto md:mx-0">
                  <SafeIcon icon={FiFileText} className="w-8 h-8" />
                </div>
                <div className="flex-grow text-center md:text-left">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Tipp: So stellen Sie die perfekte Anfrage
                  </h3>
                  <p className="text-gray-600 text-base mb-6 max-w-2xl">
                    Nutzen Sie unsere Vorlage für eine präzise Anfrage. Das spart Rückfragen, sorgt für Klarheit und beschleunigt Ihr Angebot massiv.
                  </p>
                  
                  {/* PROMINENTER BUTTON */}
                  <Link 
                    to="/blog/trockenbau-anfrage-richtig-stellen" 
                    className="inline-flex items-center justify-center bg-mk-teal-600 hover:bg-mk-teal-700 text-white font-bold py-4 px-8 rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 text-lg group"
                  >
                    <span className="mr-3">Zum kompletten Leitfaden</span>
                    <SafeIcon icon={FiArrowRight} className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
              
              {/* Eingebettete Vorlage */}
              <CopyableTemplate />
            </div>
          </motion.div>

          {/* Contact Form Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mb-12"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Kontaktformular
            </h2>
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="p-6">
                <div className="flex justify-center">
                  <iframe 
                    width="600" 
                    height="850" 
                    src="https://kundenportal.ausbau.nrw/forms/wtl/bd11f7cec082c41c3a99a80708efdffb" 
                    frameBorder="0" 
                    sandbox="allow-top-navigation allow-forms allow-scripts allow-same-origin allow-popups" 
                    allowFullScreen
                    className="w-full max-w-full h-[850px] border-0 rounded-lg"
                    title="Kontaktformular MK-AUSBAU"
                  ></iframe>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Google Reviews Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mb-12"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Das sagen unsere Kunden
            </h2>
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="elfsight-app-eab1e74e-e140-41f7-98bb-6f9ce7f910fd w-full min-h-[400px]" data-elfsight-app-lazy></div>
            </div>
          </motion.div>

          {/* Call to Action */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="text-center bg-mk-teal-50 p-8 rounded-xl"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Bereit für Ihr Projekt?
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Rufen Sie uns an oder schreiben Sie uns eine E-Mail für ein kostenloses Beratungsgespräch. Wir freuen uns darauf, Ihr Projekt gemeinsam zu verwirklichen!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:+4924528607038" 
                className="inline-flex items-center justify-center space-x-2 bg-mk-red-700 hover:bg-mk-red-800 text-white px-8 py-3 rounded-sm font-semibold transition-colors"
              >
                <SafeIcon icon={FiPhone} className="w-5 h-5" />
                <span>Jetzt anrufen</span>
              </a>
              <a 
                href="mailto:info@ausbau.nrw" 
                className="inline-flex items-center justify-center space-x-2 bg-mk-teal-500 hover:bg-mk-teal-600 text-white px-8 py-3 rounded-sm font-semibold transition-colors"
              >
                <SafeIcon icon={FiMail} className="w-5 h-5" />
                <span>E-Mail schreiben</span>
              </a>
            </div>
          </motion.div>

          {/* Emergency Service */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
            className="mt-8 p-6 bg-gradient-to-r from-mk-teal-500 to-mk-teal-600 text-white rounded-xl"
          >
            <h3 className="text-lg font-semibold mb-3">
              Notfall-Service
            </h3>
            <p className="text-mk-teal-100 text-sm mb-3">
              Bei dringenden Reparaturen und Notfällen erreichen Sie uns auch außerhalb der regulären Geschäftszeiten.
            </p>
            <p className="text-white font-semibold">
              Notfall-Hotline:{' '}
              <a href="tel:+4924528607038" className="ml-1 hover:text-mk-teal-200 transition-colors underline">
                +49 (0) 2452 8607038
              </a>
            </p>
          </motion.div>
        </div>
      </div>
      
      <ServicesNavigationFooter />
      <GoogleMaps />
    </div>
  );
}

export default Contact;